import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Form, ListGroup, Tab, Nav } from 'react-bootstrap';
import { TicketsDataService } from '../../Services/TicketsDataService';
import './TicketDetailsPage.scss';
import ImagePreviewModal from './ImagePreviewModal';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import InformationModal from '../../Components/InformationModal/InformationModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faDownload, faEye, faTrash, faUpload } from '@fortawesome/free-solid-svg-icons';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import { InteractionType } from '@azure/msal-browser';
import ConfirmationModal from '../../Components/ConfirmationModal/ConfirmationModal';

// Define supported image extensions
const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp'];

function TicketDetailsContent() {
    const { instance, accounts, inProgress } = useMsal();
    const { id } = useParams(); // Get the ticket ID from the route
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        isNewTicket: !id,
        requesterEmail: '',
        subject: '',
        typeOfHelp: '',
        urgencyLevel: '',
        contactChannel: '',
        problemDescription: '',
        files: []
    });
    const [validationErrors, setValidationErrors] = useState({});
    const [ticketComments, setTicketComments] = useState([]);
    const [ticketStatusHistory, setTicketStatusHistory] = useState([]);
    const [commentData, setCommentData] = useState('');
    const [regularComment, setRegularComment] = useState('');
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewImageUrl, setPreviewImageUrl] = useState('');
    const fileInputRef = useRef(null);
    const [showInformationModal, setShowInformationModal] = useState(false);
    const [createdTicket, setCreatedTicket] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [showSummaryModal, setShowSummaryModal] = useState(false);


    useEffect(() => {
        if (inProgress === "none" && accounts.length > 0) {
            if(!formData.isNewTicket){
                fetchTicketDetails(id);
            }
            setFormData({
                ...formData, requesterEmail: accounts[0].username
            })
        }
    }, [inProgress, accounts, instance, id]);

    const fetchTicketDetails = async (id) => {
        setTicketComments([]);;
        const request = {
            ...loginRequest,
            account: accounts[0]
        };
        const response = await instance.acquireTokenSilent(request);

        var ticket = await TicketsDataService.getTicketById(id, response.accessToken);
        if (ticket == null) return;

        var comments = await TicketsDataService.getTicketComments(id, response.accessToken);
        var ticketStatusHistory = await TicketsDataService.getTicketStatusHistory(id, response.accessToken);
        setFormData({
            ...ticket,
            isNewTicket: false
        });
        setTicketComments(comments);
        setTicketStatusHistory(ticketStatusHistory);
    }

    const fetchTicketComments = async (ticketId) => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);
            const data = await TicketsDataService.getTicketComments(ticketId, response.accessToken);
            setTicketComments(data);
        } catch (error) {
            console.error('Failed to fetch ticket comments:', error);
        }
    };

    const fetchStatusHistory = async (ticketId) => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);
            const data = await TicketsDataService.getTicketStatusHistory(ticketId, response.accessToken);
            setTicketStatusHistory(data);
        } catch (error) {
            console.error('Failed to fetch ticket status history:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: value,
        }));
        setValidationErrors((prevState) => ({
            ...prevState,
            [name]: null,
        }));
    };

    const handleFileChange = (e) => {
        setFormData((prevState) => ({
            ...prevState,
            files: Array.from(e.target.files),
        }));
    };

    const handleStatusSubmit = async (e) => {
        e.preventDefault();
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);

            await TicketsDataService.updateTicketStatus(formData.id, formData.status, commentData, response.accessToken);
            setCommentData(''); // Clear comment input
            fetchTicketComments(formData.id); // Refresh ticket comments
            fetchStatusHistory(formData.id); // Refresh ticket comments
        } catch (error) {
            console.error('Failed to update ticket status:', error);
        }
    };

    const handleAddMoreFiles = async () => {
        if (formData.files.length === 0) return;

        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);

            // Call the API to upload additional files
            await TicketsDataService.addFilesToTicket(formData.id, formData.files, response.accessToken);

            // Fetch updated ticket data (to update the file list in the modal)
            const updatedTicket = await TicketsDataService.getTicketById(formData.id, response.accessToken);
            setFormData(prevData => ({
                ...prevData,
                blobUri: updatedTicket.blobUri
            }));

            // Clear the file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

            // Optionally show a success message or notification
            console.log('Files uploaded successfully');
        } catch (error) {
            console.error('Failed to upload additional files:', error);
            // Optionally handle error or show an error message
        }
    };

    const handleValidation = () => {
        const errors = {};
        if (!formData.subject) errors.subject = 'Subject is required';
        if (!formData.typeOfHelp) errors.typeOfHelp = 'Type of help is required';
        if (!formData.urgencyLevel) errors.urgencyLevel = 'Urgency level is required';
        if (!formData.contactChannel) errors.contactChannel = 'Contact channel is required';
        if (!formData.problemDescription) errors.problemDescription = 'Problem description is required';

        setValidationErrors(errors);
        return Object.keys(errors).length === 0; // Return true if no errors
    };

    const handleSubmit = async (e) => {
        if(formData.isNewTicket) handleCreateSubmit(e);
        else handleUpdateClick(e)
    }

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        if (formData.isNewTicket && !handleValidation()) return; // If validation fails, do not proceed
    
        setIsSubmitting(true); // Set loading state to true
    
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);
    
            const result = await TicketsDataService.createTicket(formData, formData.files, response.accessToken);
            if (result && result.status === 201) {
                setCreatedTicket(result.data);
                setShowInformationModal(true);
                clearSelections();
            }
            else {
                setErrorMessage("Error updating ticket.");
                setShowErrorModal(true);
            }
        } catch (error) {
            console.error('Failed to create ticket:', error);
            setErrorMessage("There was an error while creating the ticket. Please try again.");
            setShowErrorModal(true); // Show error modal on failure
        } finally {
            setIsSubmitting(false); // Reset loading state
        }
    };

    const handleUpdateSubmit = async () => {
        // e.preventDefault();
        // if (!handleValidation()) return;
    
        setIsSubmitting(true);
    
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);
    
            const updateFormData = new FormData();
            updateFormData.append("TicketId", formData.id);
            updateFormData.append("Status", formData.status || "");
            updateFormData.append("Comment", commentData || "");
            
            // Append new files to the form data
            formData.files.forEach((file, index) => {
                updateFormData.append(`Files`, file);
            });
    
            const result = await TicketsDataService.updateTicketData(updateFormData, response.accessToken);
            if (result && result.status === 200) {
                setCommentData('');
                // Clear the file input
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                setSuccessMessage("Ticket succesfully updated");
                setShowMessageModal(true);
                fetchTicketDetails(id);
            }
            else {
                setErrorMessage("Error updating ticket.");
                setShowErrorModal(true);
            }
        } catch (error) {
            console.log(error);
            setErrorMessage("Error updating ticket.");
            setShowErrorModal(true);
        } finally {
            setIsSubmitting(false);
        }
    };
    

    const handleRegularCommentChange = (e) => {
        setRegularComment(e.target.value);
    };

    const handlePostRegularComment = async () => {
        if (regularComment.trim() === '') return;
        const request = {
            ...loginRequest,
            account: accounts[0]
        };
        const response = await instance.acquireTokenSilent(request);

        try {
            await TicketsDataService.postComment(formData.id, regularComment, response.accessToken); // Implement postComment in service
            setRegularComment(''); // Clear the comment field
            fetchTicketComments(formData.id); // Fetch updated comments
        } catch (error) {
            console.error('Failed to post comment:', error);
        }
    };

    const handleViewOrDownloadFile = async (fileUrl) => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);

            // Request SAS URI from the backend
            const fileName = fileUrl.split('/').pop()
            const sasUri = await TicketsDataService.getFileSasUri(fileName, response.accessToken);

            // Open file in a new tab
            window.open(sasUri, '_blank');
        } catch (error) {
            console.error('Failed to retrieve SAS URI for file:', error);
        }
    };

    // Method to handle file preview
    const handlePreviewFile = async (fileUrl) => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);

            // Get SAS URI for image file
            const fileName = fileUrl.split('/').pop()
            const sasUri = await TicketsDataService.getFileSasUri(fileName, response.accessToken);
            setPreviewImageUrl(sasUri);
            setShowPreviewModal(true);
        } catch (error) {
            console.error('Failed to preview file:', error);
        }
    };

    // Close preview modal
    const handleClosePreviewModal = () => {
        setShowPreviewModal(false);
        setPreviewImageUrl('');
    };

    const handleDeleteFile = async (fileUrl) => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);

            await TicketsDataService.deleteFile(formData.id, fileUrl, response.accessToken);

            // Refresh the ticket files list
            const updatedTicket = await TicketsDataService.getTicketById(formData.id, response.accessToken);
            setFormData({
                ...formData,
                blobUri: updatedTicket.blobUri
            });
        } catch (error) {
            console.error('Failed to delete file:', error);
        }
    };

    const clearSelections = () => {
        setFormData({
            isNewTicket: true,
            requesterEmail: accounts[0]?.username,
            subject: '',
            typeOfHelp: '',
            urgencyLevel: '',
            contactChannel: '',
            problemDescription: '',
            status: '',
            files: []
        });
        setCommentData('');
    }

    const handleBack = () => {
        navigate('/support');
    }

    const renderConfirmationContent = () => (
        <div>
            <h5>Updating Ticket #{id}</h5>
            <h6>Summary of Changes:</h6>
            <ul>
                {formData.files.length > 0 && (
                    <li>
                        <strong>Files to be uploaded ({formData.files.length}):</strong>
                        <ul>
                            {formData.files.map((file, index) => (
                                <li key={index}>{file.name}</li>
                            ))}
                        </ul>
                    </li>
                )}
                {formData.status && (
                    <li>
                        <strong>New Status:</strong> {formData.status}
                    </li>
                )}
                {commentData && (
                    <li>
                        <strong>Comment:</strong> {commentData}
                    </li>
                )}
            </ul>
        </div>
    );

    const handleUpdateClick = (e) => {
        e.preventDefault();
        setShowSummaryModal(true);
    };

    const handleConfirmUpdate = async () => {
        setShowSummaryModal(false);
        await handleUpdateSubmit(); // Call your submit function here
    };

    return (
        <Container fluid className="ticket-details-page d-flex flex-column flex-grow-1 h-100">
            <div className='d-flex justify-content-between align-items-end mb-1'>
                <Button variant="outline-dark" size='sm' type="button" className="" onClick={handleBack}>
                    <FontAwesomeIcon icon={faArrowLeft} className='me-2' />
                    Back to tickets
                </Button>
            </div>
            <Row className='justify-content-center ticket-info-container'>
                <Col xl={7} lg={7} md={12} className="ticket-info-form-container">
                    <h5>{formData.isNewTicket ? 'New Ticket' : `Ticket #${id}`}</h5>
                    <Form onSubmit={formData.isNewTicket ? handleSubmit : handleStatusSubmit} className="ticket-info-form">
                        {/* Main form inputs */}
                        <Row>
                            <Form.Group as={Col} controlId="requesterEmail" className="mb-3">
                                <Form.Label>Requester</Form.Label>
                                <Form.Control className="form-control-mnml" size="sm" type="text" name="requesterEmail" value={formData.requesterEmail} disabled />
                            </Form.Group>
                            <Form.Group as={Col} controlId="contactChannel" className="mb-3">
                                <Form.Label>How would you like to be contacted?</Form.Label>
                                <Form.Select className="form-control-mnml" size="sm" name="contactChannel" value={formData.contactChannel} onChange={handleChange} disabled={!formData.isNewTicket} isInvalid={!!validationErrors.contactChannel}>
                                    <option value="">Select...</option>
                                    <option value="Email">Email</option>
                                    <option value="Phone">Phone</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">{validationErrors.contactChannel}</Form.Control.Feedback>
                            </Form.Group>
                        </Row>

                        {/* Subject and description */}
                        <Row>
                            <Form.Group as={Col} controlId="requesterEmail" className="mb-3">
                                <Form.Label>Type of help</Form.Label>
                                <Form.Select className="form-control-mnml" size="sm" name="typeOfHelp" value={formData.typeOfHelp} onChange={handleChange} disabled={!formData.isNewTicket} isInvalid={!!validationErrors.typeOfHelp}>
                                    <option value="">Select...</option>
                                    <option value="Data problem">Data Problem</option>
                                    <option value="Technical issue">Technical Issue</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">{validationErrors.typeOfHelp}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group as={Col} controlId="contactChannel" className="mb-3">
                                <Form.Label>Urgency level</Form.Label>
                                <Form.Select className="form-control-mnml" size="sm" name="urgencyLevel" value={formData.urgencyLevel} onChange={handleChange} disabled={!formData.isNewTicket} isInvalid={!!validationErrors.urgencyLevel}>
                                    <option value="">Select...</option>
                                    <option value="Low">Low</option>
                                    <option value="Medium">Medium</option>
                                    <option value="High">High</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">{validationErrors.urgencyLevel}</Form.Control.Feedback>
                            </Form.Group>
                        </Row>
                        <Row>
                            <Form.Group as={Col} controlId="subject" className="mb-3">
                                <Form.Label>Subject</Form.Label>
                                <Form.Control className="form-control-mnml" size="sm" type="text" name="subject" value={formData.subject} onChange={handleChange} disabled={!formData.isNewTicket} isInvalid={!!validationErrors.subject} />
                                <Form.Control.Feedback type="invalid">{validationErrors.subject}</Form.Control.Feedback>
                            </Form.Group>
                        </Row>
                        <Row>
                            <Form.Group as={Col} controlId="problemDescription" className="mb-3">
                                <Form.Label>Describe your problem</Form.Label>
                                <Form.Control className="form-control-mnml" size="sm" as="textarea" rows={3} name="problemDescription" value={formData.problemDescription} onChange={handleChange} disabled={!formData.isNewTicket} isInvalid={!!validationErrors.problemDescription} />
                                <Form.Control.Feedback type="invalid">{validationErrors.problemDescription}</Form.Control.Feedback>
                            </Form.Group>
                        </Row>

                        {/* File Upload or Attached Files */}
                        <Form.Group controlId="files" className="mb-3 files-form-group">
                            <Form.Label>Current files</Form.Label>
                            {/* Show attached files when editing an existing ticket */}
                            {!formData.isNewTicket && (
                                <ListGroup className="files-list" style={{ minHeight: "60px" }}>
                                    {formData.blobUri && formData.blobUri.split(';').map((fileUri, index) => {
                                        const fileName = fileUri.split('/').pop();
                                        const fileExtension = fileName.split('.').pop().toLowerCase();
                                        const isImage = imageExtensions.includes(fileExtension);

                                        return (
                                            <ListGroup.Item key={index} className="d-flex justify-content-between">
                                                <span>{fileName}</span>
                                                <div>
                                                    {isImage ? (
                                                        <Button variant="outline-primary" size="sm" className="me-2" onClick={() => handlePreviewFile(fileUri)}>
                                                            <FontAwesomeIcon icon={faEye} />
                                                        </Button>
                                                    ) : (
                                                        <Button variant="outline-primary" size="sm" className="me-2" onClick={() => handleViewOrDownloadFile(fileUri)}>
                                                            <FontAwesomeIcon icon={faDownload} />
                                                        </Button>
                                                    )}
                                                    <Button variant="outline-danger" size="sm" onClick={() => handleDeleteFile(fileUri)}>
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </Button>
                                                </div>
                                            </ListGroup.Item>
                                        );
                                    })}
                                    {(!formData.blobUri || formData.blobUri.split(';').length === 0) && (
                                        <ListGroup.Item>No files attached.</ListGroup.Item>
                                    )}
                                </ListGroup>
                            )}

                            <Form.Label>Upload more files</Form.Label>
                            <Form.Control
                                className="form-control-mnml"
                                size="sm"
                                type="file"
                                multiple
                                name="additionalFiles"
                                onChange={handleFileChange}
                                ref={fileInputRef}
                            />
                        </Form.Group>
                    </Form>
                </Col>

                {!formData.isNewTicket && (
                    <Col lg={5} xl={5} md={12} className="comments-section">
                        {/* Tabs Navigation */}
                        <Tab.Container defaultActiveKey='comments'>
                            <Nav variant="pills" className="simple-pills small-pills mb-2">
                                <Nav.Item>
                                    <Nav.Link eventKey="comments">Comments</Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="statusChanges">Status Change History</Nav.Link>
                                </Nav.Item>
                            </Nav>
                            <Tab.Content className=''>
                                <Tab.Pane eventKey="comments" className='h-100'>
                                <div className="comments-container flex-grow-1 overflow-y-auto">
                                            {ticketComments && ticketComments.map((comment, index) => (
                                                <div key={index} className={`comment-container ${comment.isStatusChange ? 'align-right' : ''}`}>
                                                    <div className={`comment-bubble ${comment.isStatusChange ? 'comment-status-change' : 'comment-regular'}`}>
                                                        <div className="comment-author">{comment.authorName}</div>
                                                        <div>{comment.comment}</div>
                                                        <div className="comment-date">{new Date(comment.date).toLocaleString()}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                </Tab.Pane>

                                {/* New Tab for Status Change History */}
                                <Tab.Pane eventKey="statusChanges" className='h-100'>
                                    <div className="timeline-container flex-grow-1 overflow-y-auto">
                                        {ticketStatusHistory && ticketStatusHistory.map((statusChange, index) => (
                                            <div key={index} className="timeline-item">
                                                <div className="timeline-content">
                                                <div className="timeline-dot"></div>
                                                    <div className="timeline-status">Status changed to: {statusChange.newStatus}</div>
                                                    <div className="timeline-author">By: {statusChange.authorName}</div>
                                                    <div className="timeline-date">{new Date(statusChange.date).toLocaleString()}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </Tab.Pane>
                            </Tab.Content>
                        </Tab.Container>
                        {/* Status and comment input */}
                        {!formData.isNewTicket && (
                            <Row className='mt-4'>
                                <Form.Group controlId="status" className='mb-3'>
                                    <Form.Label>Status</Form.Label>
                                    <Form.Select className='form-control-mnml' size='sm' name="status" value={formData.status} onChange={handleChange} required>
                                        <option value="new">New</option>
                                        <option value="in progress">In Progress</option>
                                        <option value="user review">User Review</option>
                                        {/* <option value="completed">Completed</option> */}
                                        <option value="closed">Closed</option>
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group controlId="comment" className='mb-3'>
                                    <Form.Label>Comment on Status Change</Form.Label>
                                    <Form.Control className='form-control-mnml' size='sm' as="textarea" rows={3} name="comment" value={commentData} onChange={(e) => setCommentData(e.target.value)} />
                                </Form.Group>
                            </Row>
                        )}
                    </Col>
                )}
            </Row>
            <div className='d-flex justify-content-end align-items-end mt-2'>
                <small className='text-info'>You can upload files, write a comment or update the status of the ticket. Changes will only be saved after you click the Update button</small>
            </div>
            <div className='d-flex justify-content-end align-items-end mt-3'>
                <Button variant="secondary" size='lg' type="button" className="me-4" onClick={handleBack}>Cancel</Button>
                <Button variant="primary" size='lg' type="button" onClick={handleSubmit} className="" disabled={isSubmitting}>{isSubmitting ? 'Submitting...' : formData.isNewTicket ? 'Submit new ticket' : 'Update ticket'}</Button>
            </div>
            <ImagePreviewModal
                show={showPreviewModal}
                onHide={handleClosePreviewModal}
                imageUrl={previewImageUrl}
            />
            <InformationModal
                show={showInformationModal}
                onHide={() => setShowInformationModal(false)}
                onAcceptClick={() => setShowInformationModal(false)}
                title="New Ticket Created"
                content={(
                    <div>The ticket with Id #{createdTicket?.id} has been created and the administrator has been notified.</div>
                )}
            />
            <InformationModal
                show={showErrorModal}
                onHide={() => setShowErrorModal(false)}
                onAcceptClick={() => setShowErrorModal(false)}
                title="Error"
                content={<div>{errorMessage}</div>}
            />
            <InformationModal
                show={showMessageModal}
                onHide={() => setShowMessageModal(false)}
                onAcceptClick={() => setShowMessageModal(false)}
                title="Success"
                content={<div>{successMessage}</div>}
            />
            <ConfirmationModal
                show={showSummaryModal}
                onHide={() => setShowSummaryModal(false)}
                title="Confirm Changes"
                content={renderConfirmationContent()}
                onAcceptClick={handleConfirmUpdate}
                onCancelClick={() => setShowSummaryModal(false)}
            />
        </Container>
    );
}

const TicketDetailsPage = () => {
    return (
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
            <PageLayout2 title="Ticket Form">
                <TicketDetailsContent />
            </PageLayout2>
        </MsalAuthenticationTemplate>
    );
};

export default TicketDetailsPage;
